import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapContainer, TileLayer, Polyline, Marker, Popup, CircleMarker } from "react-leaflet";
import L from "leaflet";
import {
  PackageCheck, PackageX, PackageSearch, ArrowLeft, Banknote,
  RefreshCw, Trash2, ChevronRight, MapPin, Phone, User2, FileSignature, Camera,
  Navigation, Clock, Radio, X, RotateCcw, PieChart as PieIcon, Users,
  Mail, Repeat, HelpCircle,
} from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip,
  BarChart, Bar, XAxis, YAxis,
} from "recharts";
import { Courier, Routes as RoutesAPI, API, api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useConfirm } from "../components/ConfirmProvider";
import { Download, Truck, Wallet, Car } from "lucide-react";

function StatusPill({ status }) {
  if (status === "delivered")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-300">
        <PackageCheck className="w-3 h-3" /> Dostarczono
      </span>
    );
  if (status === "absent")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider bg-rose-100 text-rose-800 border border-rose-300">
        <PackageX className="w-3 h-3" /> Nieobecny
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider bg-zinc-100 text-zinc-700 border border-zinc-300">
      <PackageSearch className="w-3 h-3" /> Oczekuje
    </span>
  );
}

function MoneyPL({ value }) {
  const n = Number(value || 0);
  return (
    <span className="font-mono">
      {n.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł
    </span>
  );
}

// ---------- List of all courier routes ----------
function DeliveriesList({ embedded = false }) {
  const { user } = useAuth();
  const { confirm } = useConfirm();
  const [routes, setRoutes] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "admin";
  const isDriver = user?.role === "kierowca";

  const load = async () => {
    setLoading(true);
    try {
      if (isDriver) {
        // Portal-internal flow: read straight from route_assignments joined
        // with route_plans (no external courier backend involved).
        const data = await RoutesAPI.myDeliveries();
        setRoutes(data || []);
      } else {
        // Legacy courier proxy — may be offline; swallow errors silently
        // because the new reconciliation panel above is the primary UI.
        try {
          const data = await Courier.listRoutes();
          setRoutes(data);
        } catch {
          setRoutes([]);
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się pobrać tras");
      setRoutes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ----- Driver-facing portal-internal view --------------------------------
  if (isDriver) {
    return (
      <div className="space-y-6" data-testid="deliveries-page">
        {!embedded && (
        <div>
          <div className="font-label mb-1">// Moje trasy</div>
          <h1 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight">
            Zlecenia
          </h1>
          <p className="text-zinc-600 mt-2 text-sm max-w-xl">
            Trasy wydane Ci przez koordynatora. Pobierz manifest, zaznacz
            doręczenia i zainkasuj pobrania.
          </p>
        </div>
        )}

        <div className="grid gap-4">
          {loading && (
            <div className="text-sm text-zinc-500 py-6">Ładuję Twoje trasy…</div>
          )}
          {!loading && routes?.length === 0 && (
            <div className="p-8 bg-zinc-50 border-2 border-dashed border-zinc-300 text-center text-sm text-zinc-500"
                 data-testid="empty-my-deliveries">
              Nie masz jeszcze przypisanych tras. Poproś koordynatora o wydanie manifestu.
            </div>
          )}
          {!loading && routes?.map((r) => {
            const rate = r.total ? Math.round((r.delivered / r.total) * 100) : 0;
            const remaining = Math.max((r.cod_total || 0) - (r.cod_collected || 0), 0);
            return (
              <div key={r.assignment_id || r.plan_id}
                   data-testid={`my-delivery-${r.plan_id}`}
                   className="bg-zinc-950 text-white rounded-2xl ring-1 ring-white/10 p-5 md:p-6 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.6)]">
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="w-10 h-10 rounded-xl bg-brand/20 flex items-center justify-center ring-1 ring-brand/40">
                    <Truck className="w-5 h-5 text-brand" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-black text-lg md:text-xl uppercase tracking-tight leading-tight">
                      {r.name}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[11px] font-mono text-white/60 flex-wrap">
                      {r.assigned_at && (
                        <span>{new Date(r.assigned_at).toLocaleString("pl-PL")}</span>
                      )}
                      {r.vehicle_plate && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/10 text-white/80">
                          <Car className="w-3 h-3" /> {r.vehicle_plate}
                        </span>
                      )}
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded uppercase font-bold ${r.status === "done" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>
                        {r.status === "done" ? "Ukończona" : "Aktywna"}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      const tid = toast.loading("Generuję manifest…");
                      try {
                        const resp = await api.get(
                          `/routes/plans/${r.plan_id}/export-spoke.pdf`,
                          { responseType: "blob" },
                        );
                        const isZip = (resp.headers?.["content-type"] || "").includes("zip");
                        const ext = isZip ? "zip" : "pdf";
                        const url = URL.createObjectURL(new Blob([resp.data]));
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `${(r.name || "manifest").replace(/[^a-z0-9]+/gi, "_")}.${ext}`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        setTimeout(() => URL.revokeObjectURL(url), 5000);
                        toast.success("Pobrano manifest", { id: tid });
                      } catch (e) {
                        toast.error("Nie udało się pobrać manifestu: " + (e.response?.data?.detail || e.message), { id: tid });
                      }
                    }}
                    data-testid={`btn-download-manifest-${r.plan_id}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white text-zinc-900 text-xs font-bold uppercase tracking-wider hover:bg-zinc-100"
                  >
                    <Download className="w-3.5 h-3.5" /> Manifest PDF
                  </button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
                  <div className="rounded-xl bg-white/5 ring-1 ring-white/10 p-3">
                    <div className="text-[9px] font-mono uppercase tracking-widest text-white/60">Paczek</div>
                    <div className="text-2xl font-heading font-black mt-0.5">{r.total}</div>
                  </div>
                  <div className="rounded-xl bg-emerald-500/15 ring-1 ring-emerald-400/30 p-3">
                    <div className="text-[9px] font-mono uppercase tracking-widest text-emerald-200">Doręczone</div>
                    <div className="text-2xl font-heading font-black mt-0.5 text-emerald-200">{r.delivered}</div>
                  </div>
                  <div className="rounded-xl bg-amber-500/15 ring-1 ring-amber-400/30 p-3">
                    <div className="text-[9px] font-mono uppercase tracking-widest text-amber-200">Do zrobienia</div>
                    <div className="text-2xl font-heading font-black mt-0.5 text-amber-200">{r.pending}</div>
                  </div>
                  <div className="rounded-xl bg-indigo-500/15 ring-1 ring-indigo-400/30 p-3">
                    <div className="text-[9px] font-mono uppercase tracking-widest text-indigo-200 flex items-center gap-1">
                      <Wallet className="w-3 h-3" /> Pobrania
                    </div>
                    <div className="text-lg font-heading font-black mt-0.5 text-indigo-100">
                      {(r.cod_total || 0).toLocaleString("pl-PL", { minimumFractionDigits: 2 })} zł
                    </div>
                    {remaining > 0 && (
                      <div className="text-[10px] font-mono text-indigo-200/80">
                        zostało: {remaining.toLocaleString("pl-PL", { minimumFractionDigits: 2 })} zł
                      </div>
                    )}
                  </div>
                </div>

                {r.total > 0 && (
                  <div className="mt-4 text-[11px] font-mono text-white/60">
                    Postęp: <span className="text-white font-bold">{rate}%</span>
                    <div className="mt-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full bg-emerald-400" style={{ width: `${rate}%` }} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const remove = async (id, name) => {
    if (!(await confirm(
      `Usunąć trasę „${name}" z appki kuriera? Tej operacji nie można cofnąć.`,
      { tone: "danger", confirmLabel: "Usuń trasę" }
    ))) return;
    try {
      await Courier.removeRoute(id);
      toast.success("Trasa usunięta");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się usunąć trasy");
    }
  };

  return (
    <div className="space-y-8" data-testid="deliveries-page">
      {!embedded && (
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="font-label mb-1">// Rozliczenia zleceń</div>
          <h1 className="font-heading font-black text-4xl uppercase tracking-tight">
            Zlecenia
          </h1>
          <p className="text-zinc-600 mt-2 text-sm max-w-xl">
            Wpisz ile paczek doręczyliśmy i ile awizowaliśmy dla każdego zlecenia.
            Zatwierdzone paczki trafiają do „paczek doręczonych&rdquo; kuriera i wpadają do jego portfela.
          </p>
        </div>
        <button
          data-testid="btn-refresh-deliveries"
          onClick={load}
          className="inline-flex items-center gap-2 border border-zinc-900 px-4 py-2 text-sm font-bold uppercase tracking-wider hover:bg-zinc-900 hover:text-white transition-colors"
        >
          <RefreshCw className="w-4 h-4" /> Odśwież
        </button>
      </div>
      )}
      <UndeliveredStatsCard />

      <AssignmentsReconciliation />

      <div className="border border-zinc-900 overflow-x-auto">
        <div className="bg-zinc-900 text-white px-4 py-2 flex items-center justify-between">
          <div className="font-label text-white">// Historyczne trasy (courier legacy)</div>
        </div>
        <table className="w-full" data-testid="deliveries-table">
          <thead>
            <tr className="bg-zinc-900 text-white text-left">
              <Th>Nazwa trasy</Th>
              <Th className="text-center">Paczki</Th>
              <Th className="text-center">Status</Th>
              <Th className="text-right">Pobranie</Th>
              <Th className="text-right">Akcje</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="py-10 text-center text-zinc-500 text-sm">Ładuję trasy z appki kuriera…</td></tr>
            )}
            {!loading && routes?.length === 0 && (
              <tr><td colSpan={5} className="py-12 text-center text-zinc-500 text-sm" data-testid="empty-deliveries">
                Brak tras. Załaduj manifest w appce kuriera lub eksportuj ułożoną trasę z zakładki Trasy.
              </td></tr>
            )}
            {!loading && routes?.map((r) => (
              <tr key={r.id} className="border-t border-zinc-200 hover:bg-zinc-50" data-testid={`delivery-row-${r.id}`}>
                <td className="py-3 px-4">
                  <div className="font-semibold">{r.name}</div>
                  <div className="text-xs text-zinc-500 font-mono">
                    {r.created_at?.slice(0, 16).replace("T", " ")}
                  </div>
                </td>
                <td className="py-3 px-4 text-center font-mono">{r.stops_count}</td>
                <td className="py-3 px-4">
                  <div className="flex items-center justify-center gap-1 text-[11px] font-mono">
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300">
                      ✓ {r.delivered_count}
                    </span>
                    <span className="px-2 py-0.5 bg-rose-100 text-rose-800 border border-rose-300">
                      ✗ {r.absent_count}
                    </span>
                    <span className="px-2 py-0.5 bg-zinc-100 text-zinc-700 border border-zinc-300">
                      ⏳ {r.pending_count}
                    </span>
                  </div>
                </td>
                <td className="py-3 px-4 text-right">
                  {r.cod_total > 0 ? (
                    <div>
                      <MoneyPL value={r.cod_collected} />
                      <span className="text-zinc-400 mx-1">/</span>
                      <span className="text-zinc-500"><MoneyPL value={r.cod_total} /></span>
                    </div>
                  ) : (
                    <span className="text-zinc-400 text-xs">—</span>
                  )}
                </td>
                <td className="py-3 px-4 text-right space-x-2 whitespace-nowrap">
                  <Link
                    to={`/deliveries/${r.id}`}
                    data-testid={`open-delivery-${r.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-zinc-300 hover:border-brand hover:text-brand"
                  >
                    Szczegóły <ChevronRight className="w-3 h-3" />
                  </Link>
                  {isAdmin && (
                    <button
                      data-testid={`delete-delivery-${r.id}`}
                      onClick={() => remove(r.id, r.name)}
                      className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-bold uppercase tracking-wider border border-zinc-300 hover:border-rose-600 hover:text-rose-600"
                      title="Usuń trasę w appce kuriera"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`py-3 px-4 text-[11px] font-mono uppercase tracking-wider ${className}`}>{children}</th>
  );
}

// ---------- Live GPS track + position map for the coordinator ------------
function courierMarkerIcon() {
  return L.divIcon({
    html: `<div style="width:32px;height:32px;border-radius:50%;background:#E52C34;border:3px solid #fff;display:flex;align-items:center;justify-content:center;box-shadow:0 0 0 2px #18181b,0 0 16px rgba(229,44,52,0.6);">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5"><path d="M3 11l19-9-9 19-2-8z"/></svg>
          </div>`,
    className: "",
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
}

function fmtDuration(ms) {
  if (ms < 0 || !isFinite(ms)) return "—";
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function CourierTrackMap({ routeId }) {
  const [track, setTrack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = async () => {
    try {
      const data = await Courier.getTrack(routeId);
      setTrack(data);
    } catch (e) {
      // Silent — empty track is a legitimate state (courier hasn't started yet).
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (!autoRefresh) return undefined;
    const t = setInterval(load, 15000);  // refresh every 15s while panel is open
    return () => clearInterval(t);
  }, [routeId, autoRefresh]);

  if (loading) {
    return (
      <div className="border border-zinc-900 p-6 text-center text-zinc-500 text-sm">
        Ładuję ślad GPS...
      </div>
    );
  }

  const pings = track?.pings || [];
  if (pings.length === 0) {
    return (
      <div className="border border-amber-400 bg-amber-50 p-6 text-sm" data-testid="track-empty">
        <div className="font-label mb-1 text-amber-900">// Brak danych GPS</div>
        <p className="text-amber-900">
          Kurier jeszcze nie wysłał żadnej lokalizacji. Telefon kuriera musi mieć włączoną
          appkę z aktywną tą trasą, żeby wysyłać ślad GPS co 30 sekund.
        </p>
      </div>
    );
  }

  const linePts = pings.map((p) => [p.lat, p.lng]);
  const last = pings[pings.length - 1];
  const first = pings[0];
  const firstTs = first.timestamp ? new Date(first.timestamp) : null;
  const lastTs = last.timestamp ? new Date(last.timestamp) : null;
  const elapsedMs = firstTs && lastTs ? lastTs - firstTs : 0;
  const totalDistanceKm = (() => {
    let d = 0;
    for (let i = 1; i < pings.length; i++) {
      const a = pings[i - 1], b = pings[i];
      const R = 6371;
      const dLat = ((b.lat - a.lat) * Math.PI) / 180;
      const dLng = ((b.lng - a.lng) * Math.PI) / 180;
      const lat1 = (a.lat * Math.PI) / 180;
      const lat2 = (b.lat * Math.PI) / 180;
      const h =
        Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
      d += 2 * R * Math.asin(Math.sqrt(h));
    }
    return d;
  })();
  const lastAgoMs = lastTs ? Date.now() - lastTs.getTime() : null;
  const stale = lastAgoMs !== null && lastAgoMs > 120000;  // 2 min without update = stale

  return (
    <div className="border border-zinc-900" data-testid="track-map-section">
      <div className="px-4 py-3 bg-zinc-900 text-white flex items-center justify-between flex-wrap gap-3">
        <div className="font-label text-white flex items-center gap-2">
          <Radio className="w-3 h-3" /> Ślad GPS kuriera
          {last.courier_name && <span className="text-zinc-400">· {last.courier_name}</span>}
        </div>
        <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-wider">
          <span data-testid="track-pings-count">{pings.length} pingów</span>
          <span>{totalDistanceKm.toFixed(1)} km</span>
          <span>{fmtDuration(elapsedMs)}</span>
          <button
            data-testid="track-toggle-refresh"
            onClick={() => setAutoRefresh((v) => !v)}
            className={`px-2 py-0.5 ${autoRefresh ? "bg-emerald-500" : "bg-zinc-700"} text-white`}
          >
            {autoRefresh ? "● Live 15s" : "Live OFF"}
          </button>
        </div>
      </div>

      <div className="h-[420px]" data-testid="track-map">
        <MapContainer
          center={[last.lat, last.lng]}
          zoom={13}
          className="w-full h-full"
        >
          <TileLayer
            attribution="&copy; OpenStreetMap"
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {linePts.length > 1 && (
            <Polyline
              positions={linePts}
              pathOptions={{ color: "#E52C34", weight: 4, opacity: 0.75 }}
            />
          )}
          {/* Start dot */}
          <CircleMarker
            center={[first.lat, first.lng]}
            radius={6}
            pathOptions={{ color: "#10b981", fillColor: "#10b981", fillOpacity: 0.95, weight: 2 }}
          >
            <Popup>START · {firstTs?.toLocaleTimeString("pl-PL")}</Popup>
          </CircleMarker>
          {/* Live courier marker */}
          <Marker position={[last.lat, last.lng]} icon={courierMarkerIcon()}>
            <Popup>
              <div className="text-xs space-y-1">
                <div className="font-bold">{last.courier_name || "Kurier"}</div>
                <div>Ostatni ping: {lastTs?.toLocaleString("pl-PL")}</div>
                {last.speed_kmh != null && <div>{last.speed_kmh.toFixed(0)} km/h</div>}
                {last.accuracy != null && <div>Dokładność: ±{last.accuracy.toFixed(0)} m</div>}
              </div>
            </Popup>
          </Marker>
        </MapContainer>
      </div>

      <div className="px-4 py-2 border-t border-zinc-200 bg-zinc-50 grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px] font-mono">
        <Stat label="START" value={firstTs?.toLocaleTimeString("pl-PL") || "—"} />
        <Stat label="Ostatni ping" value={lastTs?.toLocaleTimeString("pl-PL") || "—"}
              warn={stale} warnLabel={lastAgoMs ? `${Math.floor(lastAgoMs / 60000)} min temu` : ""} />
        <Stat label="Pozycja" value={`${last.lat.toFixed(5)}, ${last.lng.toFixed(5)}`} />
        <Stat label="Prędkość" value={last.speed_kmh != null ? `${last.speed_kmh.toFixed(0)} km/h` : "—"} />
      </div>
    </div>
  );
}

function Stat({ label, value, warn, warnLabel }) {
  return (
    <div>
      <div className="text-zinc-500 uppercase tracking-wider">{label}</div>
      <div className={`font-bold ${warn ? "text-amber-700" : "text-zinc-900"}`}>{value}</div>
      {warn && warnLabel && <div className="text-amber-700 text-[9px]">⚠ {warnLabel}</div>}
    </div>
  );
}

// ---------- Single route detail ----------
function DeliveryDetail() {
  const { id } = useParams();
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setRoute(await Courier.getRoute(id));
      } catch (e) {
        toast.error(e.response?.data?.detail || "Nie udało się pobrać trasy");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div className="py-20 text-center text-zinc-500" data-testid="delivery-loading">Ładuję…</div>;
  if (!route) return <div className="py-20 text-center text-zinc-500">Trasa nie istnieje.</div>;

  const stops = [...(route.stops || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const delivered = stops.filter((s) => s.status === "delivered").length;
  const absent = stops.filter((s) => s.status === "absent").length;
  const codTotal = stops.reduce((acc, s) => acc + Number(s.cod_amount || 0), 0);
  const codCollected = stops
    .filter((s) => s.status === "delivered")
    .reduce((acc, s) => acc + Number(s.cod_amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="delivery-detail">
      <Link
        to="/zlecenia?view=map"
        className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-zinc-500 hover:text-zinc-900"
        data-testid="back-to-deliveries"
      >
        <ArrowLeft className="w-4 h-4" /> Powrót do listy
      </Link>

      <div>
        <div className="font-label mb-1">// Trasa kuriera</div>
        <h1 className="font-heading font-black text-3xl uppercase tracking-tight">{route.name}</h1>
        <div className="font-mono text-xs text-zinc-500 mt-1">{route.created_at}</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Stopy" value={stops.length} />
        <Kpi label="Dostarczono" value={delivered} accent="emerald" />
        <Kpi label="Nieobecny" value={absent} accent="rose" />
        <Kpi label="Pobranie" value={`${codCollected.toFixed(2)} / ${codTotal.toFixed(2)} zł`} accent="amber" />
      </div>

      <CourierTrackMap routeId={route.id} />

      <div className="border border-zinc-900 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-zinc-900 text-white text-left">
              <Th>#</Th>
              <Th>Adres</Th>
              <Th>Odbiorca</Th>
              <Th>Status</Th>
              <Th className="text-right">Pobranie</Th>
              <Th className="text-right">Szczegóły</Th>
            </tr>
          </thead>
          <tbody>
            {stops.map((s) => (
              <tr key={s.id} className="border-t border-zinc-200 hover:bg-zinc-50" data-testid={`stop-row-${s.id}`}>
                <td className="py-2 px-4 font-mono font-bold text-center">{s.order}</td>
                <td className="py-2 px-4 text-sm">
                  <div className="font-semibold flex items-center gap-1">
                    <MapPin className="w-3 h-3 text-brand" /> {s.address}
                  </div>
                  {s.package_numbers?.length > 0 && (
                    <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
                      {s.package_numbers.join(", ")}
                    </div>
                  )}
                </td>
                <td className="py-2 px-4 text-sm">
                  <div className="font-semibold">{s.recipient_name || "—"}</div>
                  {s.phone && <div className="font-mono text-xs text-zinc-500">{s.phone}</div>}
                </td>
                <td className="py-2 px-4"><StatusPill status={s.status} /></td>
                <td className="py-2 px-4 text-right">
                  {Number(s.cod_amount) > 0 ? (
                    <span className="font-mono font-bold">{Number(s.cod_amount).toFixed(2)} zł</span>
                  ) : (
                    <span className="text-zinc-400 text-xs">—</span>
                  )}
                </td>
                <td className="py-2 px-4 text-right">
                  {s.status === "delivered" || s.status === "absent" ? (
                    <Link
                      to={`/deliveries/${route.id}/stop/${s.id}`}
                      data-testid={`open-stop-${s.id}`}
                      className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider border border-zinc-300 px-2 py-1 hover:border-brand hover:text-brand"
                    >
                      Otwórz <ChevronRight className="w-3 h-3" />
                    </Link>
                  ) : (
                    <span className="text-zinc-400 text-xs">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  const colors = {
    emerald: "bg-emerald-50 border-emerald-300 text-emerald-900",
    rose: "bg-rose-50 border-rose-300 text-rose-900",
    amber: "bg-amber-50 border-amber-300 text-amber-900",
  };
  return (
    <div className={`border p-4 ${colors[accent] || "border-zinc-900 bg-white"}`}>
      <div className="font-label text-[10px] mb-1">{label}</div>
      <div className="font-heading font-black text-xl">{value}</div>
    </div>
  );
}

// ---------- Single stop detail (photo + signature) ----------
function DeliveryStopDetail() {
  const { routeId, stopId } = useParams();
  const [stop, setStop] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setStop(await Courier.getStop(routeId, stopId));
      } catch (e) {
        toast.error(e.response?.data?.detail || "Nie udało się pobrać stopa");
      } finally {
        setLoading(false);
      }
    })();
  }, [routeId, stopId]);

  if (loading) return <div className="py-20 text-center text-zinc-500">Ładuję…</div>;
  if (!stop) return <div className="py-20 text-center text-zinc-500">Stop nie istnieje.</div>;

  return (
    <div className="space-y-6" data-testid="stop-detail">
      <Link
        to={`/deliveries/${routeId}`}
        className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-zinc-500 hover:text-zinc-900"
        data-testid="back-to-route"
      >
        <ArrowLeft className="w-4 h-4" /> Powrót do trasy
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-label mb-1">// Stop #{stop.order}</div>
          <h1 className="font-heading font-black text-2xl uppercase tracking-tight">{stop.address}</h1>
          {stop.completed_at && (
            <div className="font-mono text-xs text-zinc-500 mt-1">
              Zakończono: {stop.completed_at.slice(0, 19).replace("T", " ")}
            </div>
          )}
        </div>
        <StatusPill status={stop.status} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border border-zinc-900 p-5 space-y-3">
          <div className="font-label">// Dane</div>
          <Field icon={User2} label="Odbiorca" value={stop.recipient_name || "—"} />
          <Field icon={Phone} label="Telefon" value={stop.phone || "—"} />
          <Field icon={Banknote} label="Pobranie" value={
            Number(stop.cod_amount) > 0 ? `${Number(stop.cod_amount).toFixed(2)} zł` : "—"
          } />
          {stop.package_numbers?.length > 0 && (
            <div>
              <div className="font-label text-[10px] mb-1">Numery paczek</div>
              <div className="font-mono text-xs space-y-1">
                {stop.package_numbers.map((p) => (
                  <div key={p} className="px-2 py-1 bg-zinc-100 inline-block mr-1">{p}</div>
                ))}
              </div>
            </div>
          )}
          {stop.note && (
            <div>
              <div className="font-label text-[10px] mb-1">Notatka kuriera</div>
              <div className="text-sm border-l-2 border-brand pl-3 italic">{stop.note}</div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {stop.photo_base64 && (
            <div className="border border-zinc-900 p-2">
              <div className="font-label mb-2 px-3 pt-2 flex items-center gap-2">
                <Camera className="w-3 h-3" /> Zdjęcie miejsca dostawy
              </div>
              <img
                src={`data:image/jpeg;base64,${stop.photo_base64}`}
                alt="dostawa"
                className="w-full border border-zinc-200"
                data-testid="stop-photo"
              />
            </div>
          )}
          {stop.signature_base64 && (
            <div className="border border-zinc-900 p-2 bg-white">
              <div className="font-label mb-2 px-3 pt-2 flex items-center gap-2">
                <FileSignature className="w-3 h-3" /> Podpis odbiorcy
              </div>
              <img
                src={`data:image/png;base64,${stop.signature_base64}`}
                alt="podpis"
                className="w-full bg-white"
                data-testid="stop-signature"
              />
            </div>
          )}
          {!stop.photo_base64 && !stop.signature_base64 && (
            <div className="border border-zinc-200 p-6 text-center text-zinc-400 text-sm">
              Brak załączników (zdjęcie / podpis) dla tego stopa.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-4 h-4 text-zinc-500 mt-0.5" />
      <div>
        <div className="font-label text-[10px]">{label}</div>
        <div className="font-semibold text-sm">{value}</div>
      </div>
    </div>
  );
}

// Top-level router-aware export
export default function Deliveries() {
  return <DeliveriesList />;
}
export { DeliveriesList, DeliveryDetail, DeliveryStopDetail };


// ---------- Admin analytics: Undelivered reason breakdown + top drivers ----------
const REASON_COLORS = {
  awizo:      "#f59e0b", // amber
  return:     "#ef4444", // red
  redelivery: "#a855f7", // purple
  unknown:    "#71717a", // zinc
};
const REASON_ICON = {
  awizo:      Mail,
  return:     RotateCcw,
  redelivery: Repeat,
  unknown:    HelpCircle,
};

function UndeliveredStatsCard() {
  const [rangeKey, setRangeKey] = useState("30"); // 7 | 30 | 90 | all
  const [driverId, setDriverId] = useState(""); // "" = all drivers
  const [drivers, setDrivers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Compute ISO date bounds from range preset
  const dateParams = useMemo(() => {
    if (rangeKey === "all") return {};
    const days = Number(rangeKey);
    const from = new Date(Date.now() - days * 24 * 3600 * 1000);
    return { from_date: from.toISOString().slice(0, 10) };
  }, [rangeKey]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { ...dateParams };
      if (driverId) params.driver_user_id = driverId;
      const d = await RoutesAPI.getUndeliveredStats(params);
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.detail || "Nie można pobrać statystyk");
    } finally {
      setLoading(false);
    }
  };

  // Load driver list once (for dropdown)
  useEffect(() => {
    RoutesAPI.listDriverRates()
      .then((d) => setDrivers(d?.drivers || []))
      .catch(() => {}); // silently no-op — filter still works without dropdown
  }, []);

  useEffect(() => { load(); }, [rangeKey, driverId]);

  const reasons = data?.reasons || [];
  const totals = data?.totals || { delivered: 0, skipped: 0, pending: 0, total: 0 };
  const topDrivers = (data?.top_drivers || []).filter((d) => d.undelivered > 0);
  const hasUndelivered = totals.skipped > 0;

  const pieData = reasons.filter((r) => r.count > 0).map((r) => ({
    name: r.label, value: r.count, id: r.id,
  }));

  return (
    <section className="border border-zinc-900 bg-white" data-testid="undelivered-stats-card">
      <div className="bg-zinc-900 text-white px-4 py-2 flex items-center gap-2 flex-wrap">
        <PieIcon className="w-4 h-4" />
        <div className="font-label text-white">// Analityka niedoręczeń</div>
        <div className="ml-auto flex items-center gap-2 text-xs">
          <label className="uppercase tracking-widest opacity-70 hidden sm:inline">Zakres:</label>
          <select
            value={rangeKey}
            onChange={(e) => setRangeKey(e.target.value)}
            data-testid="stats-range-select"
            className="bg-zinc-800 text-white border border-zinc-700 px-2 py-1 text-xs uppercase tracking-wider"
          >
            <option value="7">7 dni</option>
            <option value="30">30 dni</option>
            <option value="90">90 dni</option>
            <option value="all">Cały czas</option>
          </select>
          <select
            value={driverId}
            onChange={(e) => setDriverId(e.target.value)}
            data-testid="stats-driver-select"
            className="bg-zinc-800 text-white border border-zinc-700 px-2 py-1 text-xs max-w-[200px] truncate"
            title="Wszyscy kierowcy"
          >
            <option value="">Wszyscy kierowcy</option>
            {drivers.map((d) => (
              <option key={d.user_id} value={d.user_id}>{d.name}</option>
            ))}
          </select>
          <button
            onClick={load}
            data-testid="stats-refresh"
            className="px-2 py-1 bg-white text-zinc-900 hover:bg-zinc-200 text-xs uppercase tracking-wider font-bold flex items-center gap-1"
            title="Odśwież"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Odśwież
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 text-sm text-red-700 bg-red-50 border-b border-red-200">{error}</div>
      )}

      <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 border-b border-zinc-200">
        <TotalTile label="Wszystkie paczki" value={totals.total} icon={PackageSearch} tone="zinc" />
        <TotalTile label="Doręczone" value={totals.delivered} icon={PackageCheck} tone="emerald" />
        <TotalTile label="Niedoręczone" value={totals.skipped} icon={PackageX} tone="amber" />
        <TotalTile label="W trakcie" value={totals.pending} icon={Clock} tone="cyan" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 p-4">
        {/* Pie chart of reasons */}
        <div className="lg:col-span-3">
          <div className="font-label mb-3">// Podział powodów niedoręczenia</div>
          {!hasUndelivered ? (
            <div className="p-8 text-center text-sm text-zinc-500 border border-dashed border-zinc-300">
              W wybranym zakresie nie ma jeszcze niedoręczonych paczek.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div className="h-56" data-testid="reason-donut">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%" cy="50%"
                      innerRadius={55}
                      outerRadius={90}
                      paddingAngle={2}
                      strokeWidth={2}
                    >
                      {pieData.map((entry) => (
                        <Cell key={entry.id} fill={REASON_COLORS[entry.id] || "#71717a"} />
                      ))}
                    </Pie>
                    <RTooltip
                      formatter={(v, name) => [`${v} paczek`, name]}
                      contentStyle={{ fontSize: 12, fontFamily: "monospace" }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2">
                {reasons.map((r) => {
                  const Icon = REASON_ICON[r.id];
                  const pct = totals.skipped > 0
                    ? Math.round((r.count / totals.skipped) * 100)
                    : 0;
                  return (
                    <div
                      key={r.id}
                      data-testid={`reason-row-${r.id}`}
                      className="flex items-center gap-2 px-3 py-2 border border-zinc-200 bg-white"
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ background: REASON_COLORS[r.id] }}
                      />
                      <Icon className="w-4 h-4 text-zinc-500 shrink-0" />
                      <span className="text-sm text-zinc-800 flex-1 truncate">{r.label}</span>
                      <span className="text-sm font-bold text-zinc-900 tabular-nums">
                        {r.count}
                      </span>
                      <span className="text-xs text-zinc-500 font-mono tabular-nums w-10 text-right">
                        {pct}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Top drivers bar chart */}
        <div className="lg:col-span-2">
          <div className="font-label mb-3 flex items-center gap-1">
            <Users className="w-3 h-3" /> // Top kierowcy — niedoręczenia
          </div>
          {topDrivers.length === 0 ? (
            <div className="p-8 text-center text-xs text-zinc-500 border border-dashed border-zinc-300">
              Brak danych — nikt jeszcze nie ma niedoręczeń w tym okresie.
            </div>
          ) : (
            <div className="h-56" data-testid="top-drivers-bar">
              <ResponsiveContainer>
                <BarChart
                  data={topDrivers.slice(0, 5)}
                  layout="vertical"
                  margin={{ top: 5, right: 20, left: 5, bottom: 5 }}
                >
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fontFamily: "monospace" }} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={110}
                    tick={{ fontSize: 11, fontFamily: "monospace" }}
                  />
                  <RTooltip
                    formatter={(v, key) => [`${v} paczek`, key === "undelivered" ? "Niedoręczone" : "Doręczone"]}
                    contentStyle={{ fontSize: 12, fontFamily: "monospace" }}
                  />
                  <Bar dataKey="undelivered" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function TotalTile({ icon: Icon, label, value, tone }) {
  const map = {
    zinc:    "bg-zinc-900 text-white",
    emerald: "bg-emerald-600 text-white",
    amber:   "bg-amber-500 text-white",
    cyan:    "bg-cyan-600 text-white",
  };
  return (
    <div className={`relative overflow-hidden p-3 border border-zinc-900 ${map[tone]}`}>
      <Icon className="absolute -right-2 -bottom-3 w-14 h-14 opacity-15" strokeWidth={1.5} />
      <div className="text-[9px] font-mono uppercase tracking-widest opacity-80">{label}</div>
      <div className="text-2xl font-heading font-black mt-1 tabular-nums">{value.toLocaleString("pl-PL")}</div>
    </div>
  );
}


// ---------- Admin reconciliation panel: enter delivered + advised counts per assignment ----------
function AssignmentsReconciliation() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | pending | reconciled | cancelled

  const load = async () => {
    setLoading(true);
    try {
      // Always fetch with cancelled included so the "Anulowane" filter has data
      const data = await RoutesAPI.listAllAssignments(true);
      setRows(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie mogę pobrać zleceń");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = (rows || []).filter((a) => {
    const cancelled = a.status === "cancelled";
    if (filter === "cancelled") return cancelled;
    if (cancelled) return false; // hide cancelled unless explicitly asked
    const delivered = Number(a.approved_delivered_count || 0);
    const awizo = Number(a.approved_awizo_count || 0);
    if (filter === "pending") return delivered === 0 && awizo === 0;
    if (filter === "reconciled") return delivered > 0 || awizo > 0;
    return true;
  });

  const totals = (rows || []).filter((a) => a.status !== "cancelled").reduce((acc, a) => {
    acc.stops += Number(a.stops_count || 0);
    acc.delivered += Number(a.approved_delivered_count || 0);
    acc.awizo += Number(a.approved_awizo_count || 0);
    return acc;
  }, { stops: 0, delivered: 0, awizo: 0 });
  const cancelledCount = (rows || []).filter((a) => a.status === "cancelled").length;

  return (
    <div className="rounded-2xl bg-white ring-1 ring-zinc-200 overflow-hidden shadow-sm"
         data-testid="assignments-reconciliation">
      <div className="bg-zinc-900 text-white px-4 py-3 flex items-center gap-3 flex-wrap">
        <PackageCheck className="w-5 h-5" />
        <div className="font-label text-white text-white/90 text-[10px]">// Rozliczenia zleceń</div>
        <div className="ml-auto flex gap-2 text-[11px] font-mono uppercase tracking-widest">
          <span className="px-2 py-1 rounded-full bg-white/10 ring-1 ring-white/20">
            paczek: <b>{totals.stops}</b>
          </span>
          <span className="px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-100 ring-1 ring-emerald-300/40">
            doręczone: <b>{totals.delivered}</b>
          </span>
          <span className="px-2 py-1 rounded-full bg-amber-500/20 text-amber-100 ring-1 ring-amber-300/40">
            awizo: <b>{totals.awizo}</b>
          </span>
        </div>
      </div>

      <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200 flex items-center gap-2 flex-wrap">
        {[
          { id: "all",        label: "Aktywne" },
          { id: "pending",    label: "Nierozliczone" },
          { id: "reconciled", label: "Rozliczone" },
          { id: "cancelled",  label: `Anulowane (${cancelledCount})` },
        ].map((f) => (
          <button key={f.id}
                  onClick={() => setFilter(f.id)}
                  data-testid={`rec-filter-${f.id}`}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest transition-colors ${
                    filter === f.id ? "bg-zinc-900 text-white" : "bg-white hover:bg-zinc-200 ring-1 ring-zinc-200"
                  }`}>
            {f.label}
          </button>
        ))}
        <button onClick={load}
                data-testid="btn-refresh-reconciliation"
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest bg-white hover:bg-zinc-200 ring-1 ring-zinc-200 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Odśwież
        </button>
      </div>

      {loading && <div className="p-8 text-center text-sm text-zinc-500">Ładuję zlecenia…</div>}

      {!loading && !filtered.length && (
        <div className="p-10 text-center text-sm text-zinc-500" data-testid="rec-empty">
          Brak zleceń w wybranym filtrze.
        </div>
      )}

      {!loading && !!filtered.length && (
        <div className="divide-y divide-zinc-100">
          {filtered.map((a) => (
            <ReconciliationRow key={a.id} asg={a} onSaved={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReconciliationRow({ asg, onSaved }) {
  const [delivered, setDelivered] = useState(String(asg.approved_delivered_count ?? 0));
  const [awizo, setAwizo] = useState(String(asg.approved_awizo_count ?? 0));
  const [saving, setSaving] = useState(false);
  const { confirm, promptInput } = useConfirm();
  const stops = Number(asg.stops_count || 0);
  const total = (Number(delivered) || 0) + (Number(awizo) || 0);
  const over = stops > 0 && total > stops;
  const cancelled = asg.status === "cancelled";
  const isSettled = !!asg.settled_at;

  // Multipack payout breakdown (visible for settled/rozliczone rows)
  const approvedDelivered = Number(asg.approved_delivered_count || 0);
  const multipackExtra = Number(asg.approved_multipack_extra_count || 0);
  const fullCount = Math.max(approvedDelivered - multipackExtra, 0);
  const rate = Number(asg.effective_rate || 0);
  const extraRate = Number(asg.multipack_extra_rate || 0.5);
  const walletGain = Number(asg.wallet_gain || 0);

  const save = async () => {
    const d = Math.max(0, parseInt(delivered || "0", 10) || 0);
    const w = Math.max(0, parseInt(awizo || "0", 10) || 0);
    setSaving(true);
    try {
      await RoutesAPI.approveAssignment(asg.id, {
        approved_delivered_count: d,
        approved_awizo_count: w,
      });
      toast.success(`Zlecenie rozliczone: ${d} doręczonych, ${w} awizo`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Błąd rozliczenia");
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    if (!(await confirm(
      `Anulować zlecenie „${asg.plan_name || asg.id}"? Zniknie z listy i portfela kuriera, ale zostanie w audycie.`,
      { tone: "danger", confirmLabel: "Anuluj zlecenie" },
    ))) return;
    const reason = await promptInput("Powód anulowania (opcjonalny)", "");
    if (reason === null) return;
    try {
      await RoutesAPI.cancelAssignment(asg.id, reason || "");
      toast.success("Zlecenie anulowane");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd anulowania");
    }
  };

  const restore = async () => {
    try {
      await RoutesAPI.restoreAssignment(asg.id);
      toast.success("Zlecenie przywrócone");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd przywracania");
    }
  };



  return (
    <div
      className={`px-4 py-3 flex items-center gap-4 flex-wrap hover:bg-zinc-50 ${
        cancelled ? "opacity-50 bg-red-50/40" : ""
      }`}
      data-testid={`rec-row-${asg.id}`}
    >
      <div className="flex-1 min-w-[220px]">
        <div className={`font-semibold text-sm truncate ${cancelled ? "line-through" : ""}`}>
          {asg.plan_name || "Bez nazwy"}
        </div>
        <div className="text-[11px] font-mono text-zinc-500 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1">
            <User2 className="w-3 h-3" /> {asg.driver_name || asg.username || "—"}
          </span>
          {asg.vehicle_plate && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-900 text-white text-[10px]">
              {asg.vehicle_plate}
            </span>
          )}
          {asg.assigned_at && (
            <span>{new Date(asg.assigned_at).toLocaleDateString("pl-PL")}</span>
          )}
          <span className="text-zinc-400">· {stops} paczek</span>
          {isSettled && !cancelled && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase tracking-wider">
              rozliczone
            </span>
          )}
          {cancelled && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-100 text-red-800 text-[10px] font-bold uppercase tracking-wider"
                  title={asg.cancel_reason || ""}>
              anulowane
            </span>
          )}
        </div>
        {/* Payout breakdown — only when the assignment is settled with paid packages */}
        {isSettled && !cancelled && approvedDelivered > 0 && (
          <div
            data-testid={`payout-breakdown-${asg.id}`}
            className="mt-2 inline-flex flex-wrap items-center gap-2 text-[11px] font-mono px-2.5 py-1.5 rounded-lg bg-zinc-50 ring-1 ring-zinc-200"
            title="Jak system policzył kwotę za paczki"
          >
            <span className="inline-flex items-center gap-1 text-emerald-700">
              <PackageCheck className="w-3 h-3" />
              {fullCount} × {rate.toFixed(2)} zł
              <span className="text-zinc-500">=</span>
              <b className="text-zinc-900">{(fullCount * rate).toFixed(2)} zł</b>
            </span>
            {multipackExtra > 0 && (
              <>
                <span className="text-zinc-400">+</span>
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 font-bold uppercase tracking-wider"
                  title={`Multipack — paczki dodatkowe pod tym samym adresem, każda po ${extraRate.toFixed(2)} zł`}
                >
                  Multipack {multipackExtra} × {extraRate.toFixed(2)} zł
                  <span className="text-violet-500">=</span>
                  {(multipackExtra * extraRate).toFixed(2)} zł
                </span>
              </>
            )}
            <span className="text-zinc-400">→</span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-zinc-900 text-white">
              <Wallet className="w-3 h-3" />
              portfel <b>{walletGain.toFixed(2)} zł</b>
            </span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        {!cancelled ? (
          <>
            <NumField
              label="Doręczone"
              value={delivered}
              onChange={setDelivered}
              testId={`rec-delivered-${asg.id}`}
              accent="emerald"
            />
            <NumField
              label="Awizo"
              value={awizo}
              onChange={setAwizo}
              testId={`rec-awizo-${asg.id}`}
              accent="amber"
            />
            <button
              onClick={save}
              disabled={saving || over}
              data-testid={`btn-rec-save-${asg.id}`}
              className={`inline-flex items-center gap-1 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-widest transition-colors ${
                over
                  ? "bg-red-100 text-red-700 cursor-not-allowed"
                  : "bg-zinc-900 text-white hover:bg-brand"
              }`}
              title={over ? `Suma ${total} przekracza ${stops} paczek na trasie` : "Zapisz rozliczenie"}
            >
              {saving ? "Zapisywanie…" : over ? `Przekroczenie` : "Rozlicz"}
            </button>
            <button
              onClick={cancel}
              data-testid={`btn-rec-cancel-${asg.id}`}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-widest bg-red-50 text-red-700 hover:bg-red-100 ring-1 ring-red-200 transition-colors"
              title="Anuluj zlecenie"
            >
              <X className="w-3.5 h-3.5" /> Anuluj
            </button>
          </>
        ) : (
          <>
            <div className="text-[11px] font-mono text-red-600 max-w-[240px]">
              {asg.cancel_reason ? `Powód: „${asg.cancel_reason}"` : "Zlecenie anulowane"}
            </div>
            <button
              onClick={restore}
              data-testid={`btn-rec-restore-${asg.id}`}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-widest bg-emerald-50 text-emerald-700 hover:bg-emerald-100 ring-1 ring-emerald-200 transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Przywróć
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, testId, accent = "zinc" }) {
  const ring = accent === "emerald" ? "focus:ring-emerald-300"
             : accent === "amber"   ? "focus:ring-amber-300"
             : "focus:ring-zinc-300";
  return (
    <label className="flex flex-col items-start">
      <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-0.5">
        {label}
      </span>
      <input
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className={`w-20 px-2 py-1.5 rounded-lg border border-zinc-300 text-sm text-right font-mono outline-none focus:ring-2 ${ring}`}
      />
    </label>
  );
}
